"use client";

import { useCallback } from "react";
import {
  authClient,
  hydrateSession,
  readSession,
  SESSION_HYDRATION_FAILED_MESSAGE,
  SIGN_OUT_HYDRATION_FAILED_MESSAGE,
  ORG_SWITCH_HYDRATION_FAILED_MESSAGE,
} from "@/lib/auth";
import type { AuthSession } from "@/lib/auth";

// Re-exported for existing importers — kept as the hook's public type
// surface, though the type now lives in lib/auth.ts so hydrateSession() can
// return it without a circular import.
export type { AuthSession };

export function useAuth() {
  const { data: session, isPending, error } = authClient.useSession();

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authClient.signIn.email({ email, password });
    if (result.error) {
      throw new Error(result.error.message || "Sign in failed");
    }
    // FEAT-004: don't resolve until the session store actually reflects the
    // new session — see hydrateSession()'s doc comment in lib/auth.ts.
    const hydratedSession = await hydrateSession();
    if (!hydratedSession?.user) {
      throw new Error(SESSION_HYDRATION_FAILED_MESSAGE);
    }
    return result.data;
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    const result = await authClient.signUp.email({ email, password, name });
    if (result.error) {
      throw new Error(result.error.message || "Sign up failed");
    }
    // better-auth returns `token: null` when autoSignIn is disabled or
    // email verification is required — sign-up succeeded but no session
    // was created, so there is nothing to hydrate
    // (node_modules/better-auth/dist/api/routes/sign-up.mjs).
    if (result.data?.token) {
      const hydratedSession = await hydrateSession();
      if (!hydratedSession?.user) {
        throw new Error(SESSION_HYDRATION_FAILED_MESSAGE);
      }
    }
    return result.data;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const result = await authClient.signIn.social({
      provider: "google",
      callbackURL: "/strategies",
    });
    if (result.error) {
      throw new Error(result.error.message || "Google sign in failed");
    }
    return result.data;
  }, []);

  const signOut = useCallback(async () => {
    await authClient.signOut();
    await hydrateSession();
    // Codex review (FEAT-004 round 4): hydrateSession()'s return value
    // collapses "timed out" and "no session" into the same `null` — on a
    // timeout the store still holds the PREVIOUS (still signed-in) session,
    // so verify what the store actually holds instead of trusting
    // hydrateSession()'s return value. A /get-session error (not a
    // timeout) is fine: query.mjs's onError nulls the store's `data`, so
    // this only throws when the user is genuinely still resident.
    if (readSession()?.user) {
      throw new Error(SIGN_OUT_HYDRATION_FAILED_MESSAGE);
    }
  }, []);

  const setActiveOrganization = useCallback(async (orgId: string) => {
    await authClient.organization.setActive({ organizationId: orgId });
    await hydrateSession();
    // Same store-verification guarantee as signOut() above: a timeout or
    // error leaves activeOrganizationId stale/null, which must not read as
    // success.
    if (readSession()?.session.activeOrganizationId !== orgId) {
      throw new Error(ORG_SWITCH_HYDRATION_FAILED_MESSAGE);
    }
    // better-auth's own atomListener already flips $activeOrgSignal on any
    // successful /organization/* call (dist/plugins/organization/client.mjs),
    // but nudge it explicitly too so a mounted useActiveOrganization() (or
    // similar) settles in step with activeOrganizationId here, rather than
    // relying solely on the implicit listener.
    authClient.$store.notify("$activeOrgSignal");
  }, []);

  const activeOrganizationId =
    (session as AuthSession | null)?.session?.activeOrganizationId ?? null;

  return {
    session: session as AuthSession | null,
    isLoading: isPending,
    isAuthenticated: !!session?.user,
    activeOrganizationId,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    setActiveOrganization,
  };
}
